﻿import { useDispatch, useSelector } from "react-redux";
import { selectSchemaCollectionInfo } from "@/features/connections/integrationSlice";
import { Container, Box, Button } from "@mui/material";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import React, { useState, useEffect, useRef } from "react";
import EditQualifierISAGS from "../GlobalSettings/EditQualifiersISAGS";
import "./TransactionSetSettings.css";
import {
  selectSelectedConnection,
  setSchemaCollectionDocuments,
} from "@/features/connections/integrationSlice";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepTable from "@/components/GenericComponents/ShepTable";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const getCheckedRows = (docs) => {
  return docs.reduce(
    (acc, row) => ({
      ...acc,
      [row.EndpointSchemaId]: row.IsTransactionSettings,
    }),
    {}
  );
};

const TransactionSetSettingsGrid = () => {
  const dispatch = useDispatch();

  const schemaCollectionDocuments = useSelector(selectSchemaCollectionInfo);
  const selectedConnection = useSelector(selectSelectedConnection);

  const isagsRef = useRef();

  //Working on the fix of the checkBox and Edit
  const [checkedRows, setCheckedRows] = useState(
    getCheckedRows(schemaCollectionDocuments)
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false); // Track save process

  const [selectedTransaction, setTransaction] = useState([]);

  const modifiedTransactions = schemaCollectionDocuments.filter((row) => {
    const isChecked = checkedRows[row.EndpointSchemaId] ?? row.IsTransactionSettings;
    return isChecked !== row.IsTransactionSettings;
  });

  useEffect(() => {
    if (!isEditMode) {
      setCheckedRows(getCheckedRows(schemaCollectionDocuments));
    }
  }, [isEditMode, schemaCollectionDocuments])

  const handleCheckboxChange = (id) => (event) => {
    const newChecked = event.target.checked;

    setCheckedRows((prev) => ({
      ...prev,
      [id]: newChecked,
    }));

    const selectedRow = schemaCollectionDocuments.find(
      (row) => row.EndpointSchemaId === id
    );
    setTransaction(selectedRow);
  };

  const columns = [
    {
      field: "SchemaName",
      headerName: "Schema Name",
      width: 400,
    },
    {
      field: "Document",
      headerName: "Document",
      width: 200,
    },
    {
      field: "DocumentDescription",
      headerName: "Document Description",
      width: 330,
    },
    {
      field: "IsTransactionSettings",
      headerName: " ",
      width: 300,
      renderCell: (params) => {
        const isChecked = checkedRows[params.id] ?? params.value;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ShepCheckbox
              checked={isChecked}
              onChange={handleCheckboxChange(params.id)}
            />
            <span>Use Transaction Settings</span>
          </div>
        );
      },
    },
    {
      field: "edit",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        const isChecked =
          checkedRows[params.id] ?? params.row.IsTransactionSettings;

        const handleEditClick = () => {
          setTransaction(params.row); // Store the row data
          setIsEditMode(() => !isEditMode); // Enable Edit Mode
        };
        return (
          <ShepButton
            variant="primary"
            disabled={!isChecked || saving}
            onClick={handleEditClick}
          >
            Edit
          </ShepButton>
        );
      },
    },
  ];

  const handleSaveFunction = async () => {

    setSaving(true);
    try {
      const response = await axios.post(
        "/connectionartifactschema/save-settings-type",
        {
          connectionId: selectedConnection.ConnectionId.toString(),
          transactionSettings: modifiedTransactions.map((transaction) => ({
            isTransactionSettings:
              checkedRows[transaction.EndpointSchemaId] ??
              transaction.IsTransactionSettings,
            artifactType: transaction.ArtifactType,
            endpointSchemaId: transaction.EndpointSchemaId,
            connectionArtifactSchemaId:
              transaction.ConnectionArtifactSchemaId,
          })),
        }
      );

      if (response.data === true) {
        fetchSchemaCollectionData();
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "Transaction settings were applied successfully.",
            buttonLabel: "Close",
          })
        );
      }
    } catch (error) {
      fetchSchemaCollectionData();
      if (modifiedTransactions.length === 0) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "result",
            message: "There are no transaction setting changes to save.",
            buttonLabel: "Close",
          })
        );
      } else {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message: "Error encountered while applying the settings",
            buttonLabel: "Close",
          })
        );
      }

    } finally {
      setSaving(false);
    }
  };

  const fetchSchemaCollectionData = () => {
    axios
      .get("/connectionartifactschema", {
        params: {
          connectionId: selectedConnection.ConnectionId,
        },
      })
      .then((response) => {
        dispatch(
          setSchemaCollectionDocuments({
            schemaCollectionDocuments: response.data,
          })
        );
      })
      .catch((error) => {
        console.error("Error fetching schema collection info:", error);
      });
  };

  const handleTransactionSaveAll = async () => {
    try {
      if (!isEditMode) {
        await handleSaveFunction();
      } else {
        await isagsRef.current?.save();
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "All settings were saved successfully.",
            buttonLabel: "Close",
          })
        );
      }
    } catch (error) {
      console.error("Error during transaction save:", error);
    }
  };

  return (
    <Container>
      <FooterButton
        variant="primary"
        onClick={handleTransactionSaveAll}
        disabled={saving}
      >
        Save
      </FooterButton>
      {!isEditMode && (
        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
          <ShepTable
            tableTitle="Transaction"
            dataRows={schemaCollectionDocuments}
            columnTitles={columns}
            getRowId={(row) => row.EndpointSchemaId}
            emptyMessage={{
              title: "No Transactions.",
              message: "Add a transaction by updating the schema collection.",
            }}
          />
        </Box>)
      }
      {isEditMode && selectedTransaction && (
        <>
          <Box sx={{ pb: "30px" }}>
            <EditQualifierISAGS
              ref={isagsRef}
              isTransactionSettings={true}
              selectedTransaction={selectedTransaction}
              onSave={fetchSchemaCollectionData}
              setIsEditMode={setIsEditMode}
            />
          </Box>
        </>
      )}
    </Container>
  );
};
export default TransactionSetSettingsGrid;
